import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionManager from './SubscriptionManager';
import ReferralManager from './ReferralManager';
import ComingSoonModal from './ComingSoonModal';
import BetaAvailabilityModal from './BetaAvailabilityModal';
import { useBetaAvailability } from '../hooks/useBetaAvailability';
import configService from '../services/configService';

function OperationalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [comingSoonModal, setComingSoonModal] = useState({ isOpen: false, featureName: '' });
  const { showBetaModal, closeBetaModal } = useBetaAvailability();
  
  const businessName = businessConfig?.business_name || 'Your Business';
  
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Not Set';
    // Remove +1 country code and format as XXX-XXX-XXXX
    const cleaned = phone.replace(/^\+1/, '').replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    return phone; // Return original if not standard format
  };

  useEffect(() => {
    checkSetupStatus();
    loadSubscriptionStatus();
  }, [user]);

  const checkSetupStatus = async () => {
    try {
      // Check if user is confirmed first
      if (!user) {
        navigate('/login');
        return;
      }
      
      // Check email verification status from JWT (skip for OAuth users)
      const isOAuthUser = user?.username?.includes('Google_') || user?.username?.includes('Facebook_') || user?.username?.includes('LoginWithAmazon_') || user?.username?.includes('SignInWithApple_');
      const emailVerified = user?.attributes?.email_verified;
      
      if (!isOAuthUser && (emailVerified === false || emailVerified === 'false')) {
        console.log('Email/password user not verified, redirecting to login');
        navigate('/login');
        return;
      } else if (isOAuthUser) {
        console.log('OAuth user detected, skipping email verification check');
      }
      
      // Get phone number from business config instead of user attributes
      const cognitoUserId = user?.attributes?.sub;
      if (!cognitoUserId) {
        navigate('/setup');
        return;
      }
      
      const config = await configService.getBusinessConfigByUserId(cognitoUserId);
      setBusinessConfig(config);
      
      const phoneNumber = config?.phone_number;
      
      if (!phoneNumber) {
        navigate('/setup');
        return;
      }
      
      // Check setup completion based on config data (like Setup.js does)
      let currentStep = 1;
      
      if (config) {
        // Step 1: Business info complete?
        const step1Complete = config.business_name && config.phone_number && config.website && config.address;
        if (step1Complete) {
          currentStep = 2;
          
          // Step 2: Description complete?
          const step2Complete = config.business_description && config.assistant_name;
          if (step2Complete) {
            currentStep = 3;
            
            // Step 3: Voice & personality complete?
            const step3Complete = config.voice_settings && config.formality && config.verbosity && config.empathy;
            if (step3Complete) {
              currentStep = 4;
              
              // Step 4: Assistant phone complete?
              const step4Complete = !!config.assistant_phone;
              if (step4Complete) {
                currentStep = 5;
                
                // Step 5: Behavior settings complete?
                const step5Complete = config.manager_number && config.escalation_email;
                if (step5Complete) {
                  currentStep = 6;
                  
                  // Step 6: OAuth connections (optional) - setup complete
                  currentStep = 7; // Setup is complete
                }
              }
            }
          }
        }
      }
      
      // If setup is not complete, redirect to setup
      if (currentStep < 7) {
        // Check if all required setup steps are complete.
        const isSetupComplete = [
          // Step 1: Business info
          config.business_name && config.phone_number && config.website && config.address,
          // Step 2: Description
          config.business_description && config.assistant_name,
          // Step 3: Voice & personality
          config.voice_settings && config.formality && config.verbosity && config.empathy,
          // Step 4: Assistant phone
          !!config.assistant_phone,
          // Step 5: Behavior settings
          config.manager_number && config.escalation_email,
          // Step 6 (OAuth) is optional, so we consider it complete for this check.
          true
        ].every(Boolean);

        // If setup is not complete, redirect to the setup wizard.
        if (!isSetupComplete) {
          console.log('🔍 Dashboard: Setup not complete, redirecting to /setup.');
          navigate('/setup');
          return;
        }
      }
    } catch (error) {
      console.warn('Could not load config status, redirecting to setup:', error);
      navigate('/setup');
      return;
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const status = await configService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (error) {
      console.warn('Could not load subscription status:', error);
    }
  };

  const testCall = () => {
    const assistantPhone = businessConfig?.assistant_phone;
    if (assistantPhone) {
      window.location.href = `tel:${assistantPhone}`;
    } else {
      alert('Assistant phone number not configured. Please complete setup first.');
    }
  };

  if (loading) {
    return (
      <>
        <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
          <div className="text-xl font-bold text-primary">
            <Link to="/" className="text-primary no-underline">Remote Assistant</Link>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={logout} className="text-primary hover:text-primary-hover bg-transparent border-none cursor-pointer">
              Sign Out
            </button>
          </div>
        </nav>
        <div className="pt-24 pb-8 px-8 max-w-4xl mx-auto text-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-8"></div>
          <h2 className="text-2xl font-semibold text-gray-900">Loading dashboard...</h2>
        </div>
      </>
    );
  }

  return (
    <>
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div className="text-xl font-bold text-primary">
          <Link to="/" className="text-primary no-underline">Remote Assistant</Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/setup" className="text-primary hover:text-primary-hover no-underline">Setup</Link>
          <button onClick={logout} className="text-primary hover:text-primary-hover bg-transparent border-none cursor-pointer">
            Sign Out
          </button>
        </div>
      </nav>

      <div className="pt-24 pb-8 px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Welcome back, {businessName}!
          </h1>
          <p className="text-lg text-gray-600">Your AI assistant is ready to help your business</p>

        </div>

        <div className="grid gap-8 grid-cols-1 md:grid-cols-2 mb-12">
          {/* Assistant Status */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Assistant Status</h2>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Active</h3>
                <p className="text-gray-600">Your assistant is ready to take calls</p>
              </div>
            </div>
            
            {/* Assistant Phone Number - Prominent Display */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6 text-center">
              <p className="text-sm font-medium text-blue-600 mb-2">Your Assistant Phone Number</p>
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900 tracking-wider whitespace-nowrap">
                {formatPhoneNumber(businessConfig?.assistant_phone)}
              </div>
            </div>
            
            <button onClick={testCall} className="w-full px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold shadow">
              📞 Call Assistant
            </button>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="flex flex-col gap-2">
              <Link to="/setup" className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold shadow text-center no-underline block">
                Modify Setup
              </Link>
              <button 
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold shadow"
                onClick={() => setComingSoonModal({ isOpen: true, featureName: 'View Call Logs' })}
              >
                View Call Logs
              </button>
              <button 
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold shadow"
                onClick={() => setComingSoonModal({ isOpen: true, featureName: 'Business Settings' })}
              >
                Business Settings
              </button>
            </div>
          </div>
        </div>

        {/* Subscription Management */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-2xl font-bold text-gray-900">
            Subscription Management
          </h2>
          <SubscriptionManager />
        </div>

        {/* Referral Program */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-2xl font-bold text-gray-900">
            Referral Program
          </h2>
          <ReferralManager />
        </div>
      </div>
      
      <ComingSoonModal
        isOpen={comingSoonModal.isOpen}
        onClose={() => setComingSoonModal({ isOpen: false, featureName: '' })}
        featureName={comingSoonModal.featureName}
      />
      
      <BetaAvailabilityModal 
        isOpen={showBetaModal}
        onClose={closeBetaModal}
        email={user?.attributes?.email}
      />
    </>
  );
}

export default OperationalDashboard;